import { STATUS_MAP, COUPON_STATUS, SUCCESS_COUPON_STATUS } from './enums';
import { BENEFIT_DEEP_LINK_BASE_URL } from './constants';

export const formatDate = (dateStr) => {
    if (!dateStr) return '';
    if (dateStr.includes('-') && dateStr.split('-')[0].length === 2) {
      return dateStr.replace(/-/g, '/');
    }
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return dateStr;
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  export const shortDateFormat = (dateStr) => {
    if (!dateStr) return '';
    if (dateStr.includes('/')) {
      const [year, month] = dateStr.split('/').reverse();
      return `${month}/${year}`;
    }
    if (dateStr.includes('.')) {
      const [year, month] = dateStr.split('.').reverse();
      return `${month}/${year}`;
    }
    return dateStr;
  };

  const getCurrentBudget = (benefit, coupon) => {
    const multipassType = benefit?.multipassType ?? benefit?.multipass_type;
    switch (multipassType) {
      case 1:
      case 2:
        return coupon.currentBudget ?? coupon.current_budget;
      default:
        return null;
    }
  };
  
  const getPricePaid = (benefit, coupon) => {
    const multipassType = benefit?.multipassType ?? benefit?.multipass_type;
    switch (multipassType) {
      case 0:
        return benefit.priceAfterDiscount || benefit.price_after_discount || coupon.boughtAtSum || coupon.bought_at_sum;
      case 1:
        return coupon.boughtAtSum || coupon.bought_at_sum || coupon.price;
      case 2:
        return coupon.discountPrice ?? coupon.discount_price;
      default:
        return null;
    }
  };

  const normalizeCoupon = (coupon) => ({
    id: coupon.id,
    couponCode: coupon.couponCode ?? coupon.coupon_code,
    expirationDate: coupon.expirationDate ?? coupon.expiration_date,
    originalBudget: coupon.originalBudget ?? coupon.original_budget,
    orderDate: coupon.orderDate ?? coupon.order_date,
    orderId: coupon.orderId ?? coupon.order_id,
    is_archived: coupon.is_archived ?? coupon.isArchived ?? false,
    isExpired: coupon.isExpired ?? coupon.is_expired ?? false,
    currentBudget: coupon.currentBudget ?? coupon.current_budget,
    isCancelled: coupon.isCancelled ?? coupon.is_cancelled ?? false,
    isFullyUsed: coupon.isFullyUsed ?? coupon.is_fully_used ?? false,
    status: coupon.status,
    boughtAtSum: coupon.boughtAtSum ?? coupon.bought_at_sum,
    discountPrice: coupon.discountPrice ?? coupon.discount_price,
    price: coupon.price,
  });

  export const transformPaidCoupons = (benefit, coupons) => {
    const arr = Array.isArray(coupons) ? coupons : [];
    return arr.map((coupon) => {
      const norm = normalizeCoupon(coupon);
      return {
        id: norm.id,
        code: norm.couponCode,
        status: getPaidCouponStatus(benefit, norm),
        validity: norm.expirationDate ? shortDateFormat(norm.expirationDate) : '',
        price: getPricePaid(benefit, norm),
        originalPrice: norm.originalBudget,
        orderDate: norm.orderDate?.replace(/\./g, '/'),
        orderNumber: norm.orderId,
        is_archived: norm.is_archived,
        isExpired: norm.isExpired,
        currentBudget: getCurrentBudget(benefit, norm),
        isExternalLinkCoupon: isExternalLinkCoupon(norm.couponCode),
      };
    });
};

const getPaidCouponStatus = (benefit, coupon) => {
  const multipassType = benefit?.multipassType ?? benefit?.multipass_type;
  if (coupon.isCancelled)
    return COUPON_STATUS.REFUND;
  else if (coupon.isFullyUsed)
    return COUPON_STATUS.FULLY_USED;
  else if (coupon.currentBudget != null && multipassType !== 0) {
    const originalBudget = coupon.originalBudget ?? coupon.original_budget;
    if (originalBudget != null && coupon.currentBudget < originalBudget){
      return COUPON_STATUS.PARTIALLY_USED;
    }
    if (coupon.currentBudget === 0)
      return COUPON_STATUS.FULLY_USED;
  }
  else if (coupon.status === 0) return COUPON_STATUS.SUCCESS;
  else if (coupon.isExpired && coupon.status === SUCCESS_COUPON_STATUS)
    return COUPON_STATUS.EXPIRED;
  return STATUS_MAP[coupon.status] ?? COUPON_STATUS.SUCCESS;
};

const getFreeCouponStatus = (item) => {
  if (item.is_expired || item.isExpired)
    return COUPON_STATUS.EXPIRED;
  return STATUS_MAP[item.status];
};

export const transformFreeCoupon = (item, orderId, orderDate) => {
    return {
      id: item.item_id,
      code: item.coupon_code,
      status: getFreeCouponStatus(item),
      validity: item.expiration_date ? shortDateFormat(item.expiration_date) : '',
      price: item.price_paid,
      originalPrice: item.original_price,
      orderDate: item.purchase_date ? item.purchase_date.replace(/-/g, '/') : orderDate?.replace(/\./g, '/'),
      orderNumber: orderId,
      is_archived: item.is_archived || false,
      isExpired: item.is_expired || item.isExpired || false,
    };
};
  
export const transformApiData = (response) => {
  const items = response?.items;
  
  if (!items || !Array.isArray(items)) return { benefits: [], hasNext: false };
  
  const benefits = items.map((benefit) => {
    return {
      id: benefit.benefit_id,
      name: benefit.title,
      image: benefit.image,
      count: benefit.total_coupons,
      totalSavings: benefit.total_savings,
      latest_order_date: benefit.latest_order_date,
      isMultipass: benefit.is_multipass === 1 || benefit.is_multipass === 2,
      isExpired: benefit.is_expired || benefit.isExpired || false,
      multipassType: benefit.is_multipass,
      orders: benefit.orders ? benefit.orders : [],
      priceBeforeDiscount: benefit.is_multipass !== 2 ? Number(benefit.price_before_discount) : null,
      priceAfterDiscount: benefit.is_multipass !== 2 ? Number(benefit.price_after_discount) : null,
      shouldRedirectToBenefitScreen: benefit.shouldRedirectToBenefitScreen
    };
  });
  
  return {
    benefits,
    hasNext: response?.hasNext ?? false,
    totalActiveBenefitsCount: response?.totalActiveBenefitsCount ?? 0,
    totalCancelledBenefitsCount: response?.totalCancelledBenefitsCount ?? 0,
    totalArchivedBenefitsCount: response?.totalArchivedBenefitsCount ?? 0,
  };
};

export const handleBenefitNameClick = (benefit) => {
  if (benefit.shouldRedirectToBenefitScreen) {
    window.open(`${BENEFIT_DEEP_LINK_BASE_URL}/${benefit.id}`, '_blank');
  }
};

export const isExternalLinkCoupon = (couponCode) => {
  return couponCode && (couponCode.startsWith('http') || couponCode.startsWith('https'));
};

/** Extracts coupons array from getUserCouponsForBenefit API response (supports both results and items) */
export const getCouponsFromBenefitResponse = (response) => {
  const arr = response?.results ?? response?.items;
  return Array.isArray(arr) ? arr : [];
};

export const padId = (id) => {
  return String(id).padStart(9, '0');
};